import { CrossIcon } from 'assets/icons';
import Button from 'components/button';
import StyledInput from 'components/styledInput/StyledInput';
import { useState } from 'react';
import ApiDataBase from 'server';
import {
  TASK_CHECK_TIMEOUT_MS,
  TASK_LABEL_MAX_LENGTH,
  addPendingTask,
  applyTaskCheckResult,
  cleanTaskLabel,
  countTemplateTasks,
  createPendingTaskKey,
  getAddedTaskItems,
  getQualificationTaskRows,
  isTaskLabelListed,
  readTaskCheckAnswer,
  removeCompanyTask,
  removePendingTask,
} from './templateFormData';
import { templateText } from './templateTexts';

const JOB_TITLE_MAX_LENGTH = 120;

/**
 * Asks the AI to check an added task and settles it in the form once, on the
 * first of the answer and the timeout. setForm is the wizard's setter: the
 * answer still lands when the admin has moved to another step meanwhile.
 */
const checkAddedTask = ({ key, label, jobTitle, setForm }) => {
  let settled = false;
  let timer = null;
  const settle = (answer) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    const result = readTaskCheckAnswer(answer);
    setForm((prev) => applyTaskCheckResult(prev, key, result));
  };
  timer = setTimeout(() => settle(null), TASK_CHECK_TIMEOUT_MS);
  const title = cleanTaskLabel(jobTitle).slice(0, JOB_TITLE_MAX_LENGTH);
  ApiDataBase.postAdminCheckMissionTemplateTask(
    { label, ...(title ? { jobTitle: title } : {}) },
    settle,
    () => settle(null)
  );
};

/** The tasks of a job description: the qualification's, ticked or not, and
 *  the ones the admin adds on top. */
const TemplateTasksSection = ({ form, setForm }) => {
  const [newLabel, setNewLabel] = useState('');
  const rows = getQualificationTaskRows(form);
  const addedItems = getAddedTaskItems(form);
  const { selected, total } = countTemplateTasks(form);
  const cleanLabel = cleanTaskLabel(newLabel);
  const notice = form.taskNotice;

  const clearNotice = () =>
    setForm((prev) => (prev.taskNotice ? { ...prev, taskNotice: null } : prev));

  const addTask = () => {
    if (!cleanLabel) return;
    if (isTaskLabelListed(cleanLabel, form)) {
      setForm((prev) => ({ ...prev, taskNotice: { type: 'duplicate' } }));
      return;
    }
    const key = createPendingTaskKey();
    setForm((prev) => addPendingTask(prev, cleanLabel, key));
    setNewLabel('');
    checkAddedTask({
      key,
      label: cleanLabel,
      jobTitle: form.qualificationLabel,
      setForm,
    });
  };

  const removeTask = (item) =>
    setForm((prev) =>
      item.pending
        ? removePendingTask(prev, item.key)
        : removeCompanyTask(prev, item.label)
    );

  const toggleTask = (row) =>
    setForm((prev) => ({
      ...prev,
      taskChecks: { ...(prev.taskChecks || {}), [row.key]: !row.checked },
    }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        <span className="ft-xl font-medium text-gray-900">
          {templateText('tasks.title')}
        </span>
        <span className="ft-sm text-gray-500">
          {templateText('tasks.description')}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-end gap-2">
          <div className="flex-1 min-w-0">
            <StyledInput
              type="text"
              label={
                <span className="sr-only">
                  {templateText('tasks.addPlaceholder')}
                </span>
              }
              value={newLabel}
              maxLength={TASK_LABEL_MAX_LENGTH}
              onChange={(event) => {
                setNewLabel(event.target.value);
                clearNotice();
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                addTask();
              }}
              placeholder={templateText('tasks.addPlaceholder')}
            />
          </div>
          {/* Centred on the 40 px input, below its screen-reader label row. */}
          <div className="h-10 flex items-center">
            <Button
              size="SM"
              color={cleanLabel ? 'SECONDARY' : 'DISABLED'}
              onClick={addTask}
              disabled={!cleanLabel}
            >
              {templateText('tasks.addButton')}
            </Button>
          </div>
        </div>
        {notice?.type === 'duplicate' ? (
          <span className="ft-sm text-red-500">
            {templateText('tasks.duplicate')}
          </span>
        ) : null}
        {notice?.type === 'rejected' ? (
          <span className="ft-sm text-red-500">
            {templateText('tasks.rejected', { label: notice.label })}
          </span>
        ) : null}
      </div>

      <span className="text-sm text-gray-700">
        <strong>
          {templateText(
            selected > 1
              ? 'tasks.selectedCountOther'
              : 'tasks.selectedCountOne',
            { count: selected }
          )}
        </strong>{' '}
        {templateText(
          selected > 1 ? 'tasks.selectedOfOther' : 'tasks.selectedOfOne',
          { total }
        )}
      </span>

      {rows.length === 0 && addedItems.length === 0 ? (
        <span className="text-sm text-gray-500">
          {templateText('tasks.noProposal')}
        </span>
      ) : (
        <ul className="flex flex-col gap-2">
          {addedItems.map((item) => (
            <li
              key={item.key}
              aria-busy={item.pending ? 'true' : undefined}
              className={`flex items-center justify-between gap-3 px-3 py-2 border-2 ${
                item.pending
                  ? 'border-dashed border-gray-300'
                  : 'border-yellow-500'
              }`}
            >
              <div className="flex flex-col min-w-0">
                <span
                  className={`text-sm break-words ${
                    item.pending ? 'text-gray-400' : 'text-gray-900'
                  }`}
                >
                  {item.label}
                </span>
                <span className="text-sm text-gray-400">
                  {templateText(
                    item.pending ? 'tasks.checking' : 'tasks.addedByCompany'
                  )}
                </span>
              </div>
              <button
                type="button"
                onClick={() => removeTask(item)}
                className="flex items-center justify-center w-8 h-8 shrink-0 rounded-lg hover:bg-gray-100"
              >
                <span className="sr-only">
                  {templateText('tasks.remove', { label: item.label })}
                </span>
                <span aria-hidden="true">
                  <CrossIcon wh={20} color="#374151" />
                </span>
              </button>
            </li>
          ))}
          {rows.map((row) => (
            <li key={row.key}>
              <label
                className={`flex items-center gap-3 px-3 py-2 border-2 cursor-pointer ${
                  row.checked ? 'border-yellow-500' : 'border-gray-100'
                }`}
              >
                <input
                  type="checkbox"
                  className="checkbox-primary"
                  checked={row.checked}
                  onChange={() => toggleTask(row)}
                />
                <span className="text-sm text-gray-900 break-words">
                  {row.label}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TemplateTasksSection;
